import React, { useState } from "react";
import { useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";
import { toast } from "sonner-native";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { MemberAccordionSelectInput } from "@/blocks/MemberAccordionSelectInput";
import { VnForm } from "@/components/VnForm";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { OTHER_STATUS_MAP, OtherStatusEnum } from "@/domain/applications";
import { HouseholdMemberUpdateType } from "@/domain/householdMember";
import {
  OtherStatusFormType,
  useOtherStatusForm,
} from "@/hooks/useMemberConditionsAndStatusForm";
import { useTranslator } from "@/hooks/useTranslate";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { getOtherStatusFieldLabel } from "@/utils/applications";

export const ApplicationOtherStatusesScreen = () => {
  const { t } = useTranslator();
  const { form, householdMembers } = useOtherStatusForm();
  const [isLoading, setIsLoading] = useState(false);

  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);

  const router = useRouter();

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();

  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const handleSubmit = async (formData: OtherStatusFormType) => {
    if (!householdMembers) {
      return;
    }

    setIsLoading(true);

    const updatePromises = householdMembers.map(member => {
      const updateData = formData[member.id] as HouseholdMemberUpdateType;

      return updateHouseholdMember({
        id: member.id,
        householdMemberData: updateData,
      });
    });

    const results = await Promise.allSettled(updatePromises);

    const failures = results.filter(result => result.status === "rejected");
    if (failures.length > 0) {
      setIsLoading(false);
      return toast.error(
        t(
          "native:applicationOtherStatusesScreen.errors.unableToUpdateHouseholdMembers",
        ),
      );
    }

    setIsLoading(false);

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "OTHER_STATUSES",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/tax-information",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      isNextButtonLoading={isLoading}
      onNextButtonPress={() => {
        void form.handleSubmit(handleSubmit)();
      }}
      titleI18nKey="native:applicationOtherStatusesScreen.title"
      descriptionI18nKey="native:applicationOtherStatusesScreen.description">
      <VnForm form={form} isInputOnlyForm>
        {householdMembers && (
          <MemberAccordionSelectInput
            options={Object.values(OtherStatusEnum).map(status => ({
              optionName: status,
              optionValue: OTHER_STATUS_MAP[status],
              i18nKey: getOtherStatusFieldLabel(status),
            }))}
            members={householdMembers}
          />
        )}
      </VnForm>
    </ApplicationScreenLayout>
  );
};
